import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  FRAMING_OVERLAY_CLASS_LABELS,
  FRAMING_OVERLAY_PRESETS,
  type FramingOverlayClass,
  type FramingOverlayPresetKey,
} from "./framingOverlayPresets";

const PRESET_LABELS: Record<FramingOverlayPresetKey, string> = {
  subject_safety: "Subject safety",
  plan_vs_applied: "Plan vs applied",
  transform_lineage: "Transform lineage",
  effects: "Effects",
  none: "None (pixels only)",
  advanced: "Advanced",
};

/**
 * Overlay presets as data (CROP-WB-009). Advanced exposes individual
 * overlay classes without making nine unlabeled boxes the default.
 */
export function FramingOverlayPresetMenu({
  preset,
  onPresetChange,
  advancedClasses,
  onAdvancedClassesChange,
}: {
  preset: FramingOverlayPresetKey;
  onPresetChange: (preset: FramingOverlayPresetKey) => void;
  advancedClasses: readonly FramingOverlayClass[];
  onAdvancedClassesChange: (classes: FramingOverlayClass[]) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select
        value={preset}
        onValueChange={(value) =>
          onPresetChange(value as FramingOverlayPresetKey)
        }
      >
        <SelectTrigger
          size="sm"
          className="min-w-44"
          aria-label="Overlay preset"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {(Object.keys(PRESET_LABELS) as FramingOverlayPresetKey[]).map(
            (key) => (
              <SelectItem key={key} value={key}>
                {PRESET_LABELS[key]}
              </SelectItem>
            ),
          )}
        </SelectContent>
      </Select>
      {preset === "advanced" ? (
        <fieldset className="flex flex-wrap items-center gap-2">
          <legend className="sr-only">Individual overlay classes</legend>
          {FRAMING_OVERLAY_PRESETS.advanced.map((overlayClass) => {
            const checked = advancedClasses.includes(overlayClass);
            return (
              <label
                key={overlayClass}
                className="flex items-center gap-1 rounded border px-2 py-0.5 text-xs"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(event) =>
                    onAdvancedClassesChange(
                      event.target.checked
                        ? [...advancedClasses, overlayClass]
                        : advancedClasses.filter(
                            (item) => item !== overlayClass,
                          ),
                    )
                  }
                />
                {FRAMING_OVERLAY_CLASS_LABELS[overlayClass]}
              </label>
            );
          })}
        </fieldset>
      ) : null}
    </div>
  );
}
